import {
    AnimateHeight,
    Button,
    formatNumber,
    pickValuesStartWith,
    replaceStr,
    Select,
    SelectableBox
} from 'flos-react-cvi';
import React from 'react';
import { connect } from 'react-redux';
import { CoverageOption } from '../components/coverage-option';
import {
    selectCoverages,
    selectPermanentInjuryAmountOptions,
    selectDisabilityCoverageAmount,
    selectDisabilityCoverageAmountDisplay,
    selectPrimaryType,
    selectResources,
    selectUpdatingCoverage
} from '../store/selectors';
import { focusSection, selectInjuryAmount, toggleCoverageSelection } from '../store/thunks';

const AccidentCoveragesView = ({
    isPrimary,
    requiredCoverages,
    optionalCoverages,
    showOptionalCoverages,
    resources,
    onSelectInjuryAmount,
    injuryAmountOptions,
    disabilityCoverageAmount,
    disabilityCoverageAmountDisplay,
    toggleCoverageSelectionProp,
    updatingCoverage,
    disabled,
    focusSectionProp
}) => {
    const [injuryOptionsShown, setShowInjuryOptions] = React.useState(false);

    function showInjuryOptions() {
        setShowInjuryOptions(true);
        focusSectionProp();
    }

    const injuryDescriptions = React.useMemo(
        () =>
            pickValuesStartWith(resources, 'coveragePermanentInjuryAmount\\.description\\.').map(({ value, key }) => ({
                key,
                value: disabilityCoverageAmount
                    ? replaceStr(value, {
                          '{eight_pct}': formatNumber(0.08 * disabilityCoverageAmount),
                          '{twenty_pct}': formatNumber(0.2 * disabilityCoverageAmount),
                          '{eighty_pct}': formatNumber(0.8 * disabilityCoverageAmount)
                      })
                    : value
            })),
        [resources, disabilityCoverageAmount]
    );

    const mapCoverageOption = coverage => (
        <CoverageOption
            {...coverage}
            onToggle={toggleCoverageSelectionProp(coverage.objectCoverageId)}
            isPrimary={isPrimary}
            isLoading={coverage.objectCoverageId === updatingCoverage}
            disabled={disabled}
            resources={resources}
            key={coverage.nameId}
        />
    );

    return (
        <>
            {requiredCoverages.map(mapCoverageOption)}
            <AnimateHeight height={showOptionalCoverages ? 'auto' : 0}>
                {optionalCoverages.map(mapCoverageOption)}
                <div className="u-Space-up--lg">
                    <h4 className="u-font-weight--normal">{resources.coveragePermanentInjuryTitle}</h4>
                    <p>
                        {replaceStr(resources.coveragePermanentInjuryDescription, {
                            '{amount}': disabilityCoverageAmountDisplay
                        })}
                    </p>
                    {injuryOptionsShown ? (
                        <>
                            <Select
                                value={disabilityCoverageAmount}
                                onChangeValue={onSelectInjuryAmount}
                                className="visible-xs"
                                disabled={disabled}
                                data-testid="injury-coverage-select"
                            >
                                {injuryAmountOptions.map(({ key, value }) => (
                                    <option value={key} key={key}>
                                        {value}
                                    </option>
                                ))}
                            </Select>
                            <div className="row hidden-xs">
                                {injuryAmountOptions.map(({ key, value }) => {
                                    return (
                                        <div className={`col-sm-${12 / injuryAmountOptions.length}`} key={key}>
                                            <SelectableBox
                                                active={key === String(disabilityCoverageAmount)}
                                                headerText={value}
                                                onSelect={() => onSelectInjuryAmount(key)}
                                                disabled={disabled}
                                                data-testid={`injury-coverage-box-${key}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <ul className="u-Space-up--lg">
                                {injuryDescriptions.map(({ value, key }) => (
                                    <li key={key}>{value}</li>
                                ))}
                            </ul>
                        </>
                    ) : (
                        <p>
                            {resources.coveragePermanentInjuryAmount} <b>{disabilityCoverageAmountDisplay}</b>{' '}
                            <Button variant="link" onClick={showInjuryOptions}>
                                {resources.coveragePermanentInjuryAmountCustomize}
                            </Button>
                        </p>
                    )}
                </div>
            </AnimateHeight>
        </>
    );
};

const mapStates = state => {
    const isPrimary = selectPrimaryType(state) === 'accident';
    const coverages = selectCoverages(state);
    const updatingCoverage = selectUpdatingCoverage(state);
    const requiredCoverages = coverages.accident.filter(coverage => coverage.required);
    const optionalCoverages = coverages.accident.filter(coverage => !coverage.required);
    const showOptionalCoverages = isPrimary || requiredCoverages.some(coverage => coverage.chosen);

    return {
        isPrimary,
        updatingCoverage,
        requiredCoverages,
        optionalCoverages,
        showOptionalCoverages,
        resources: selectResources(state),
        disabilityCoverageAmount: selectDisabilityCoverageAmount(state),
        injuryAmountOptions: selectPermanentInjuryAmountOptions(state),
        disabilityCoverageAmountDisplay: selectDisabilityCoverageAmountDisplay(state),
        disabled: updatingCoverage !== null
    };
};

const mapDispatch = dispatch => ({
    toggleCoverageSelectionProp: objectCoverageId => () => dispatch(toggleCoverageSelection(objectCoverageId)),
    focusSectionProp: () => dispatch(focusSection(1)),
    onSelectInjuryAmount: amountString => dispatch(selectInjuryAmount(Number(amountString)))
});

export const AccidentCoverages = connect(mapStates, mapDispatch)(AccidentCoveragesView);
